import { AdminLayout } from "./AdminLayout";
import { Plus, Search, Filter, MoreVertical, Mail, Phone, Building2, Eye } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', source: 'website', message: ''
  });

  const fetchLeads = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/crm/leads`, { params, withCredentials: true });
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, [statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        await axios.put(`${API}/crm/leads/${editingLead.lead_id}`, formData, { withCredentials: true });
        toast.success("Lead updated");
      } else {
        await axios.post(`${API}/crm/leads`, formData, { withCredentials: true });
        toast.success("Lead created");
      }
      setIsDialogOpen(false);
      setEditingLead(null);
      setFormData({ name: '', email: '', phone: '', company: '', source: 'website', message: '' });
      fetchLeads();
    } catch (error) {
      toast.error("Failed to save lead");
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name, email: lead.email, phone: lead.phone || '',
      company: lead.company || '', source: lead.source, message: lead.message || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/crm/leads/${leadId}`, { withCredentials: true });
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/crm/leads/${leadId}`, { status }, { withCredentials: true });
      toast.success("Status updated");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statusColors = {
    new: 'badge-new', contacted: 'badge-contacted', qualified: 'badge-qualified',
    proposal: 'badge-proposal', won: 'badge-won', lost: 'badge-lost'
  };

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.company || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Leads">
      <div className="space-y-6" data-testid="admin-leads">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Leads</h2>
            <p className="text-sm text-[#8A8A8A]">{leads.length} total</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button 
                className="btn-primary text-sm flex items-center gap-2"
                onClick={() => { setEditingLead(null); setFormData({ name: '', email: '', phone: '', company: '', source: 'website', message: '' }); }}
                data-testid="add-lead-btn"
              >
                <Plus className="w-4 h-4" /> Add Lead
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
              <DialogHeader>
                <DialogTitle>{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Name *" required value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="input-field" data-testid="lead-name-input" />
                  <input type="email" placeholder="Email *" required value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="input-field" data-testid="lead-email-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input type="tel" placeholder="Phone" value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="input-field" />
                  <input type="text" placeholder="Company" value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="input-field" />
                </div>
                <select value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})}
                  className="input-field">
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social">Social Media</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="other">Other</option>
                </select>
                <textarea placeholder="Message/Notes" rows={3} value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="input-field resize-none" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm" data-testid="save-lead-btn">
                    {editingLead ? 'Update' : 'Create'} Lead
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input type="text" placeholder="Search leads..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10" data-testid="search-leads" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-full sm:w-48" data-testid="filter-status">
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="proposal">Proposal</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>

        {/* Table */}
        <div className="border border-[#2E2E2E] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1C1C1C] border-b border-[#2E2E2E]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Lead</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Contact</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Source</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Created</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-[#2E2E2E]">
                      <td colSpan={6} className="p-4"><div className="h-4 bg-[#2E2E2E] rounded animate-pulse" /></td>
                    </tr>
                  ))
                ) : filteredLeads.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-[#8A8A8A]">No leads found</td></tr>
                ) : (
                  filteredLeads.map((lead) => (
                    <tr key={lead.lead_id} className="border-b border-[#2E2E2E] hover:bg-[#1C1C1C]" data-testid={`lead-row-${lead.lead_id}`}>
                      <td className="p-4">
                        <div className="font-medium">{lead.name}</div>
                        {lead.company && <div className="text-sm text-[#8A8A8A] flex items-center gap-1"><Building2 className="w-3 h-3" />{lead.company}</div>}
                      </td>
                      <td className="p-4">
                        <div className="text-sm flex items-center gap-1"><Mail className="w-3 h-3 text-[#8A8A8A]" />{lead.email}</div>
                        {lead.phone && <div className="text-sm text-[#8A8A8A] flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</div>}
                      </td>
                      <td className="p-4 text-sm capitalize">{lead.source.replace('_', ' ')}</td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className={`badge ${statusColors[lead.status]} cursor-pointer`}>{lead.status}</button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                            {['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'].map((status) => (
                              <DropdownMenuItem key={status} onClick={() => updateStatus(lead.lead_id, status)}
                                className="capitalize cursor-pointer hover:bg-[#2E2E2E]">{status}</DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                      <td className="p-4 text-sm text-[#8A8A8A]">{new Date(lead.created_at).toLocaleDateString('en-GB')}</td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                            <DropdownMenuItem asChild className="cursor-pointer hover:bg-[#2E2E2E]">
                              <Link to={`/admin/leads/${lead.lead_id}`}><Eye className="w-4 h-4 mr-2" /> View Details</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(lead)} className="cursor-pointer hover:bg-[#2E2E2E]">Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(lead.lead_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Leads;
